/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
/* global fetch */

import React from "react";
import { connect } from "react-redux";
import get from "lodash/get";
import PropTypes from "prop-types";
import { UpdateOnInterval, SocialShare, AccessType } from "@quintype/components";
import { StoryPageCard } from "../../../story-page-components/story-page-card.js";
import TimeAgo from "react-timeago";
import { HeroImage } from "../../../atoms/hero-image";
import {
  getHorizontalSocialShare,
  getPrimaryColor,
  getSocialShareStyle,
  getOverride,
  gridSelectorTemplate,
  accessCheck,
  getInfiniteScrollConfigStyle,
  selectedStoryTemplate
} from "../../../../utils";
import { getSocialShareTemplate } from "../../../story-page-components/social-share/get-social-template";
import { getAccessData, handleAccess, checkIfStoryPublic } from "../../../sso-components/sso-utils";
import { SvgHandler } from "../../../svg-handler/svg-handler";
import clockIcon from "../../../../../assets/icons/clock.svg";
import { StorySection } from "../../../atoms/story-section";
import { StoryHeadline } from "../../../atoms/story-headline/index.js";
import { HeroImageCaption } from "../../../atoms/hero-image-caption/index.js";
import { StorySubheadline } from "../../../atoms/story-subheadline";
import { Author } from "../../../basic/author.js";
import { Share } from "../../../molecules/share";
import { AdService } from "../../../ad-service.js";
import { StoryFooter } from "../../../molecules/story-footer";
import { StoryTags } from "../../../story-page-components/story-tags.js";
import format from "date-fns/format";
import { KeyEventsSidebar } from "../../../molecules/key-events-sidebar/key-events-sidebar";
import { ImageFallback } from "../../../basic/image-fallback";
import liveIcon from "../../../../../assets/icons/social/liveIcon.svg";
import { addTargetBlankToExtLinks } from "../../../../../utils/add-target-blank-to-extlinks.js";
import {
  sectionGrid,
  headlineGrid,
  imageGrid,
  imageCaptionGrid,
  subheadlineGrid,
  metypeGrid,
  socialTagsGrid,
  horizontalLineGrid
} from "../../story-selectors";
import "./live-blog.module.css";

class LiveBlogTemplate extends React.Component {
  constructor(props) {
    super(props);
    const cdnImage = get(props, ["config", "cdn-image"], "");
    this.cdnImage = cdnImage;
  }
  componentDidMount() {
    const { checkAccess, story, accessUpdated, accessIsLoading, config, firstStory } = this.props;
    const getAccess = () => getAccessData(checkAccess, story, accessUpdated, accessIsLoading, config, firstStory);
    this.props.initAccessType(getAccess);
  }
  getCardTemplate = liveBlog => {
    switch (liveBlog) {
      case "hero-priority":
        return "grid-4-12-1";
      case "headline-hero-priority":
        return "grid-4-12-1";
      case "headline-priority":
        return "grid-3-10";
      case "headline-sideway":
        return "grid-4-12-1";
      case "hero-overlay":
        return "grid-4-12-1";
      default:
        return "grid-2-10";
    }
  };
  dateFormatter(value, unit, suffix, date) {
    return format(new Date(date), "HH:mm, DD MMM YYYY");
  }

  getStoryCardTemplate = (cards = []) => {
    const socialShareStyle = getHorizontalSocialShare(this.props.config);
    const primaryColor = getPrimaryColor(this.props.config);
    const liveBlog = selectedStoryTemplate("liveBlog-story", this.props.story, this.props.config);
    const hasMultiCards = cards.length > 1;
    const infiniteScrollStyle = getInfiniteScrollConfigStyle(this.props.config);
    const firstStoryCard = get(this.props, ["story", "cards", 0]);
    if (infiniteScrollStyle === "read-more" && !this.props.firstStory) {
      return (
        <div className={`story-cards ${this.getCardTemplate(liveBlog)}`}>
          <StoryPageCard
            key={firstStoryCard.index}
            card={firstStoryCard}
            config={this.props.config}
            story={this.props.story}
            firstStory={this.props.firstStory}
            cdn={this.cdnImage}
          />
        </div>
      );
    } else {
      return cards.map((card, index) => (
        <div
          data-testid="live-blog-story-card"
          className={`story-cards ${this.getCardTemplate(liveBlog)}`}
          id={card.id}
          key={index}
        >
          <div data-testid="live-blog-social-share-wrapper" styleName="live-blog-social-share-wrapper">
            <div styleName="clock-wrapper">
              <span styleName="clock-time-wrapper">
                <div styleName="clock">
                  <SvgHandler width="16px" height="16px" xlinkHref={clockIcon} />
                </div>
                <TimeAgo
                  date={card["card-added-at"]}
                  title={null}
                  styleName="card-added-at"
                  formatter={this.dateFormatter}
                />
              </span>
            </div>
            <SocialShare
              url={`${this.props.story.slug}?cardId=${card.id}`}
              fullUrl={get(this.props, ["story", "url"], "")}
              title={
                card.metadata
                  ? get(card, ["metadata", "social-share", "title"], "")
                  : get(this.props, ["story", "title"], "")
              }
              template={getSocialShareTemplate(socialShareStyle)}
              primaryColor={primaryColor}
              hashtags={get(this.props, ["story", "tags"], [])
                .map(tag => tag.name)
                .toString()}
            />
          </div>
          <StoryPageCard
            key={index}
            card={card}
            config={this.props.config}
            story={this.props.story}
            firstStory={this.props.firstStory}
            cdn={this.cdnImage}
          />
          <p className="card-updated-at" data-testid="live-blog-last-updated">
            Last updated <TimeAgo date={card["card-updated-at"]} formatter={this.dateFormatter} title={null} />
          </p>
          {index === 0 && hasMultiCards && (
            <div className="app-ad app-ad--story-horizontal grid-column-full">
              <AdService adtype="Story-Middle-Ad" slotId="Story-Middle-Horizontal-LiveBlog" />
            </div>
          )}
        </div>
      ));
    }
  };
  render() {
    const liveBlogList = {
      "hero-priority": true,
      "headline-priority": true,
      "headline-hero-priority": true,
      default: false
    };
    const { access: storyAccess, accessLoading } = this.props;
    const isStoryPublic = checkIfStoryPublic(this.props.story);
    const socialShareStyle = getSocialShareStyle(this.props.config);
    const { "is-closed": isClosed } = get(this.props, ["story", "metadata"], {});
    const liveBlog = selectedStoryTemplate("liveBlog-story", this.props.story, this.props.config);
    const infiniteScrollStyle = get(this.props, ["config", "theme-attributes", "infiniteScrollStyle"]);
    const readTime = get(this.props, ["story", "read-time"]);
    const {
      "last-published-at": date,
      "first-published-at": firstpublishedTime,
      cards: storyCards = [],
      authors: authorsArray = [
        {
          name: "author-name",
          id: "author-id"
        }
      ]
    } = this.props.story;
    const horizontalShare = liveBlogList[liveBlog] && socialShareStyle.includes("horizontal");
    const publishedTime = date || firstpublishedTime;
    // classes for template
    const liveBlogTemplates = {
      "hero-priority": "hero-priority-template",
      "headline-priority": "headline-priority-template",
      "headline-hero-priority": "headline-hero-priority-template",
      "headline-sideway": "headline-sideway-template",
      "hero-overlay": "hero-overlay-template"
    };
    const getLiveBlogClasses = new Proxy(liveBlogTemplates, getOverride(""));
    const getLiveBlogIcon = isClosed => {
      const storyTemplate = get(this.props, ["story", "story-template"], "");
      return (
        storyTemplate === "live-blog" &&
        !isClosed && (
          <span data-testid="live-blog-story-icon" styleName="live-story-wrapper">
            <SvgHandler width="24px" height="24px" viewBox="0 0 24 24" clazzName="liveIcon" xlinkHref={liveIcon} />
          </span>
        )
      );
    };
    const getSocialShare = liveBlog => {
      switch (liveBlog) {
        case "hero-priority":
          return (
            <Share
              story={this.props.story}
              config={this.props.config}
              grey={socialShareStyle.includes("grey")}
              vertical={socialShareStyle.includes("vertical")}
            />
          );
        case "hero-overlay":
          return (
            <Share
              story={this.props.story}
              config={this.props.config}
              grey={socialShareStyle.includes("grey")}
              vertical
            />
          );
        case "headline-hero-priority":
          return (
            <Share
              story={this.props.story}
              config={this.props.config}
              grey={socialShareStyle.includes("grey")}
              vertical={socialShareStyle.includes("vertical")}
            />
          );
        case "headline-priority":
          return (
            <Share
              story={this.props.story}
              config={this.props.config}
              grey={socialShareStyle.includes("grey")}
              vertical={socialShareStyle.includes("vertical")}
            />
          );
        case "headline-sideway":
          return (
            <Share
              story={this.props.story}
              config={this.props.config}
              grey={socialShareStyle.includes("grey")}
              vertical
            />
          );
        default:
          return <Share story={this.props.story} config={this.props.config} grey={socialShareStyle.includes("grey")} />;
      }
    };

    const getAuthor = props => {
      switch (liveBlog) {
        case "hero-priority":
          return (
            <Author
              config={props.config}
              authors={authorsArray}
              isLink
              showDateOnly
              date={publishedTime}
              story={liveBlog}
              readTime={readTime}
            />
          );
        case "hero-overlay":
          return (
            <Author
              config={props.config}
              authors={authorsArray}
              isLink
              showDateOnly
              date={publishedTime}
              story={liveBlog}
              readTime={readTime}
              liveBlog={liveBlog}
            />
          );
        default:
          return (
            <Author
              config={this.props.config}
              authors={authorsArray}
              isLink
              showDateOnly
              date={publishedTime}
              readTime={readTime}
            />
          );
      }
    };
    const aspectRatio = {
      "hero-priority": [16, 9],
      "headline-priority": [16, 9],
      "headline-hero-priority": [16, 9],
      "headline-sideway": [4, 3],
      "hero-overlay": [16, 9]
    };
    const desktopAspectRatio = new Proxy(aspectRatio, getOverride([8, 3]));
    const mobileAspectRatio = new Proxy({}, getOverride([16, 9]));
    const getSideAd = new Proxy(
      { "hero-priority": false, "headline-hero-priority": false, "headline-sideway": false, "hero-overlay": false },
      getOverride(true)
    );

    /* Horizontal Social-Share Author */
    const supportHorizontalAuthorGrid = {
      default: "grid-col-2-7",
      "hero-priority": "grid-col-4-8",
      "headline-priority": "grid-col-3-8 grid-row-7-8",
      "headline-hero-priority": "grid-col-4-9",
      "headline-sideway": "grid-col-2-8 grid-row-5-6"
    };
    const horizontalAuthorStyle = supportHorizontalAuthorGrid[liveBlog]
      ? supportHorizontalAuthorGrid[liveBlog]
      : supportHorizontalAuthorGrid["default"];

    /* Vertical Social-Share Author */
    const supportAuthorGrid = {
      default: "grid-col-2-7",
      "hero-priority": "grid-col-4-12",
      "headline-priority": "grid-col-3-10 grid-row-7-8",
      "headline-hero-priority": "grid-col-4-12",
      "headline-sideway": "grid-col-2-8 grid-row-5-6"
    };

    const authorStyle = supportAuthorGrid[liveBlog] ? supportAuthorGrid[liveBlog] : supportAuthorGrid["default"];

    /* Horizontal Social-Share */
    const supportHorizontalShareGrid = {
      default: "grid-col-7-10",
      "hero-priority": "grid-col-8-12",
      "headline-priority": "grid-col-8-10 grid-row-6-7",
      "headline-hero-priority": "grid-col-9-12",
      "headline-sideway": "grid-col-2-3 grid-row-9-auto",
      "hero-overlay": "grid-col-2-3 grid-row-9-auto"
    };

    const horizontalShareStyle = supportHorizontalShareGrid[liveBlog]
      ? supportHorizontalShareGrid[liveBlog]
      : supportHorizontalShareGrid["default"];

    /* Vertical Social-Share */
    const supportVerticalShareGrid = {
      default: "grid-col-7-10",
      "hero-priority": "grid-col-2-3 grid-row-3-auto",
      "headline-priority": "grid-col-2-3 grid-row-6-auto",
      "headline-hero-priority": "grid-col-2-3 grid-row-6-auto",
      "headline-sideway": "grid-col-2-3 grid-row-9-auto",
      "hero-overlay": "grid-col-2-3 grid-row-9-auto"
    };
    const verticalShareStyle = supportVerticalShareGrid[liveBlog]
      ? supportVerticalShareGrid[liveBlog]
      : supportVerticalShareGrid["default"];
    const haveFocusPoint = !!get(this.props, ["story", "hero-image-metadata", "focus-point"]);
    function getImageTemplate(props) {
      const image = get(props, ["story", "hero-image-s3-key"]);

      if (image) {
        return (
          <div styleName={`image-wrapper ${!haveFocusPoint ? "" : "full-height"}`}>
            <HeroImage
              story={props.story}
              desktopAspectRatio={desktopAspectRatio[liveBlog]}
              mobileAspectRatio={mobileAspectRatio[liveBlog]}
            />
          </div>
        );
      }

      if (liveBlog === "hero-overlay" && !image) {
        return (
          <div styleName="fallback-image">
            <ImageFallback />
          </div>
        );
      }

      return <div />;
    }

    const liveBlogStoryClassMappings = {
      "hero-overlay": "simple"
    };
    const section = liveBlogStoryClassMappings[liveBlog] || "";

    return (
      <article
        styleName={`live-blog-wrapper ${getLiveBlogClasses[liveBlog]} ${horizontalShare ? "horizontal" : ""}`}
        className="blank-story grid"
      >
        {/* hero image */}
        <div
          styleName="hero-image"
          data-testid="live-blog-hero-image"
          className={gridSelectorTemplate(imageGrid, liveBlog)}
        >
          {getImageTemplate(this.props)}
        </div>
        <div styleName="caption" className={gridSelectorTemplate(imageCaptionGrid, liveBlog)}>
          <HeroImageCaption story={this.props.story} />
        </div>
        {/* Section */}
        <div styleName="grid-item item" item className={gridSelectorTemplate(sectionGrid, liveBlog)}>
          <StorySection story={this.props.story} config={this.props.config} template={section} />
        </div>
        {/* Headline */}
        <div styleName="grid-item headline item" className={gridSelectorTemplate(headlineGrid, liveBlog)}>
          {getLiveBlogIcon(isClosed)}

          <StoryHeadline headline={this.props.story.headline} headlineWhite={liveBlog === "hero-overlay"} />
        </div>
        {/* Subheadline */}
        <div styleName="grid-item subheadline item" className={gridSelectorTemplate(subheadlineGrid, liveBlog)}>
          <StorySubheadline subheadline={this.props.story.subheadline} subheadlineWhite={liveBlog === "hero-overlay"} />
        </div>

        {/* Author */}
        <div styleName="grid-item author" className={`${horizontalShare ? horizontalAuthorStyle : authorStyle}`}>
          {getAuthor(this.props)}
        </div>

        {/* Social Share  */}
        <div
          styleName="grid-item social-share"
          className={`${horizontalShare ? horizontalShareStyle : verticalShareStyle}`}
        >
          <div styleName="sticky">{getSocialShare(liveBlog)} </div>
        </div>
        {/* Horizontal Line */}
        <div
          data-testid="live-blog-horizontal-line"
          styleName="grid-item horizontal-line"
          className={gridSelectorTemplate(horizontalLineGrid, liveBlog)}
        />
        {(infiniteScrollStyle !== "read-more" || (infiniteScrollStyle === "read-more" && this.props.firstStory)) && (
          /* Key Events and Ad Service */
          <div styleName="grid-item key-event-ad-wrapper">
            <KeyEventsSidebar
              story={this.props.story}
              config={this.props.config}
              showAd={getSideAd[liveBlog]}
              index={this.props.index}
            />
          </div>
        )}

        {/* story cards */}
        {isStoryPublic
          ? this.getStoryCardTemplate(storyCards, this.props.firstStory)
          : handleAccess(this.props.story, storyAccess, accessLoading, this.props.config, cards =>
              this.getStoryCardTemplate(cards, this.props.firstStory)
            )}

        {/* StoryTags */}
        <div
          data-testid="live-blog-tags"
          styleName="grid-item"
          className={gridSelectorTemplate(socialTagsGrid, liveBlog)}
        >
          <StoryTags tags={this.props.story.tags} config={this.props.config} />
        </div>
        {/* Metype + Ad */}
        <div data-testid="live-blog-metype" className={gridSelectorTemplate(metypeGrid, liveBlog)}>
          {!this.props.preview && <StoryFooter story={this.props.story} config={this.props.config} />}
        </div>
      </article>
    );
  }
}
LiveBlogTemplate.propTypes = {
  preview: PropTypes.bool,
  story: PropTypes.object,
  config: PropTypes.object,
  checkAccess: PropTypes.func,
  accessUpdated: PropTypes.func,
  accessIsLoading: PropTypes.func,
  access: PropTypes.object,
  firstStory: PropTypes.bool,
  initAccessType: PropTypes.func,
  accessLoading: PropTypes.bool,
  index: PropTypes.number
};
class LiveBlogStoryBase extends React.Component {
  componentDidMount() {
    addTargetBlankToExtLinks(this.storyWrapperElement);
  }
  getStoryData = async () => {
    const storyId = get(this.props, ["story", "id"]);
    const url = `/api/v1/stories/${storyId}`;
    const response = await (await fetch(url)).json();
    return response;
  };
  render() {
    const email = get(this.props, ["member", "email"]);
    const phone = get(this.props, ["member", "metadata", "phone-number"], "");
    const storyId = get(this.props, ["story", "id"]);

    const themeAttributes = get(this.props, ["config", "theme-attributes"]);
    const PbAccessTypeConfig = get(this.props, ["config", "pagebuilder-config", "general", "accesstypeIntegration"]);
    const accessTypeKey = get(PbAccessTypeConfig, ["accesstypeKey"], get(themeAttributes, ["accesstypeKey"]));
    const accessTypeBkIntegrationId = get(
      PbAccessTypeConfig,
      ["bridgeKeeperIntegrationId"],
      get(themeAttributes, ["accesstypeBkIntegrationId"], "")
    );
    const isStaging =
      get(PbAccessTypeConfig, ["accesstypeMode"]) === "staging" ||
      get(themeAttributes, ["accesstypeEnvironment"]) === "staging";

    const enableAccesstype = get(
      PbAccessTypeConfig,
      ["enableAccesstype"],
      get(themeAttributes, ["enableAccesstype"], false)
    );
    const disableMetering = get(
      PbAccessTypeConfig,
      ["disableMetering"],
      get(themeAttributes, ["disableMetering"]) === "true"
    );

    const accessTypeProdHost = get(PbAccessTypeConfig, ["accessTypeProdHost"]);

    const accessTypeStgHost = get(PbAccessTypeConfig, ["accessTypeStgHost"]);

    return (
      <AccessType
        enableAccesstype={accessCheck(enableAccesstype)}
        isStaging={isStaging}
        accessTypeKey={accessTypeKey}
        accessTypeBkIntegrationId={accessTypeBkIntegrationId}
        email={email}
        phone={phone}
        disableMetering={disableMetering}
        storyId={storyId}
        prodHost={accessTypeProdHost}
        stagingHost={accessTypeStgHost}
      >
        {({ initAccessType, checkAccess, accessUpdated, accessIsLoading }) => (
          <div className={`story-grid`} ref={el => (this.storyWrapperElement = el)}>
            <UpdateOnInterval dataLoader={this.getStoryData} interval={20000} initData={{ story: this.props.story }}>
              {({ data }) => {
                const clonedProps = Object.assign({}, this.props, data);
                return (
                  <LiveBlogTemplate
                    accessIsLoading={accessIsLoading}
                    accessUpdated={accessUpdated}
                    initAccessType={initAccessType}
                    checkAccess={checkAccess}
                    {...clonedProps}
                    config={this.props.config}
                    firstStory={this.props.firstStory}
                  />
                );
              }}
            </UpdateOnInterval>
          </div>
        )}
      </AccessType>
    );
  }
}

LiveBlogStoryBase.propTypes = {
  story: PropTypes.object,
  config: PropTypes.object,
  initAccessType: PropTypes.func,
  firstStory: PropTypes.bool
};

const mapStateToProps = state => ({
  config: get(state, ["qt", "config"], {}),
  member: get(state, ["member"], null),
  access: get(state, ["access"], null),
  accessLoading: get(state, ["accessLoading"], true)
});
export const LiveBlogStory = connect(mapStateToProps)(LiveBlogStoryBase);
export default LiveBlogStory;
